import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import { LOAN_APP_STATUS_MAP } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';

export default function LoanApplicationList() {
  const [apps, setApps] = useState([]);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  
  const { hasPermission } = useAuth();

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [editForm, setEditForm] = useState({
    loanProduct: '',
    requestedAmount: '',
    duration: '',
    purpose: '',
    status: '',
    notes: ''
  });
  const [editLoading, setEditLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => { fetchApps(); fetchProducts(); }, [page, search, statusFilter]);

  const fetchApps = async () => {
    setLoading(true);
    try {
      let url = `/loans/applications?page=${page}&search=${search}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await api.get(url);
      setApps(res.data.data.applications);
      setPagination(res.data.data.pagination);
    } catch (err) {
      console.error("Failed to fetch applications", err);
    }
    setLoading(false);
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get('/loan-products?isActive=true');
      setProducts(res.data.data?.products || res.data.data || []);
    } catch (err) { /* silent fail */ }
  };

  const statusMap = {
    ...LOAN_APP_STATUS_MAP,
    'disbursing': { label: 'Disbursing', color: 'info' },
    'disbursement_failed': { label: 'Disb. Failed', color: 'danger' }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const res = await api.get(`/loans/applications?search=${search}&limit=10000`);
      const allApps = res.data.data.applications || [];

      if (allApps.length === 0) {
        alert("No data to export");
        setIsExporting(false);
        return;
      }

      const escapeCsv = (val) => {
        const str = String(val ?? '');
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = ["App No (National ID)", "Client Name", "Phone", "Product", "Requested Amount", "Approved Amount", "Status", "Created Date"];
      const csvRows = [];
      csvRows.push(headers.map(escapeCsv).join(','));

      allApps.forEach(app => {
        const clientName = app.client ? `${app.client.firstName} ${app.client.lastName}` : 'N/A';
        const productName = app.loanProduct?.name || 'N/A';
        
        const row = [
          escapeCsv(app.applicationNumber || ''),
          escapeCsv(clientName),
          escapeCsv(app.client?.phone || ''),
          escapeCsv(productName),
          app.requestedAmount || 0,
          app.approvedAmount || 0,
          escapeCsv(app.status),
          escapeCsv(app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '')
        ];
        csvRows.push(row.join(','));
      });

      const csvString = csvRows.join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `loan_apps_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export data.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("⚠️ WARNING: Are you sure you want to permanently delete this loan application?\n\nThis action cannot be undone.")) return;
    
    try {
      await api.delete(`/loans/applications/${id}`);
      if (apps.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchApps();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete application.");
    }
  };

  const openEditModal = (app) => {
    setEditingApp(app);
    setEditForm({
      loanProduct: app.loanProduct?._id || '',
      requestedAmount: app.requestedAmount || '',
      duration: app.duration || '',
      purpose: app.purpose || '',
      status: app.status || '',
      notes: app.reviewNotes || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const payload = {
        ...editForm,
        requestedAmount: parseFloat(editForm.requestedAmount) || 0,
        duration: parseInt(editForm.duration) || 1,
      };
      
      await api.put(`/loans/applications/${editingApp._id}`, payload);
      setShowEditModal(false);
      fetchApps();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update application.");
    } finally {
      setEditLoading(false);
    }
  };

  const renderActions = (row) => {
    const isAdmin = hasPermission('loans', 'delete');
    let statusButton = null;

    if (row.status === 'draft') {
      statusButton = (
        <button type="button" className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); navigate('/loans/applications/new', { state: { applicationId: row._id } }); }}>
          ✏️ Continue
        </button>
      );
    }
    else if (['submitted', 'under-review'].includes(row.status) && hasPermission('loans', 'approve')) {
      statusButton = (
        <button type="button" className="btn btn-info btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/loans/applications/${row._id}/review`); }}>
          👁️ Review
        </button>
      );
    }
    else if (row.status === 'approved' && hasPermission('loans', 'update')) {
      statusButton = (
        <button type="button" className="btn btn-success btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/loans/applications/${row._id}/review`); }}>
          💰 Disburse
        </button>
      );
    }
    else if (row.status === 'disbursing') {
      statusButton = <span className="text-sm text-muted" style={{fontWeight: 600}}>⏳ Processing M-Pesa...</span>;
    }
    else if (row.status === 'disbursed') {
      if (row.convertedToLoan) {
        statusButton = (
          <button type="button" className="btn btn-success btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/loans/${row.convertedToLoan}`); }}>
            📊 View Active Loan
          </button>
        );
      } else {
        statusButton = (
          <button type="button" className="btn btn-warning btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/loans/applications/${row._id}/review`); }}>
            🔧 Finalize
          </button>
        );
      }
    }
    else if (row.status === 'disbursement_failed') {
      statusButton = (
        <button type="button" className="btn btn-warning btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/loans/applications/${row._id}/review`); }}>
          🔄 Retry / Fix
        </button>
      );
    }

    return (
      <div className="flex gap-1 flex-wrap" style={{ justifyContent: 'flex-end' }}>
        {statusButton}
        {isAdmin && (
          <>
            <button 
              type="button" 
              className="btn btn-warning btn-sm" 
              onClick={(e) => { e.stopPropagation(); openEditModal(row); }}
              title="Admin Edit"
            >
              ✏️
            </button>
            <button 
              type="button" 
              className="btn btn-danger btn-sm" 
              onClick={(e) => { e.stopPropagation(); handleDelete(row._id); }}
              title="Admin Delete"
            >
              🗑️
            </button>
          </>
        )}
      </div>
    );
  };

  const allStatuses = ['draft', 'submitted', 'under-review', 'approved', 'rejected', 'withdrawn', 'disbursing', 'disbursed', 'disbursement_failed'];

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex gap-2 items-center">
          <h2>Loan Applications</h2>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/loans/disbursements')} style={{ fontSize: '13px' }}>
            💰 Disbursement History
          </button>
        </div>
        <div className="flex gap-2">
           <button 
            className="btn btn-secondary" 
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : '📥 Export CSV'}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/loans/applications/new')}>+ New Application</button>
        </div>
      </div>

      <div className="mb-3 flex gap-2" style={{ alignItems: 'center' }}>
        <input 
          className="form-control" 
          style={{ maxWidth: '400px' }} 
          placeholder="Search by National ID, Client Name, or Phone..." 
          value={search} 
          onChange={(e) => { setSearch(e.target.value); setPage(1); }} 
        />
        <select 
          className="form-control" 
          style={{ maxWidth: '200px' }} 
          value={statusFilter} 
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          {allStatuses.map(s => <option key={s} value={s}>{s.replace(/-/g, ' ').toUpperCase()}</option>)}
        </select>
        {statusFilter && (
          <button className="btn btn-sm btn-outline" onClick={() => { setStatusFilter(''); setPage(1); }}>Clear</button>
        )}
      </div>
      
      <div className="card">
        {loading ? <div className="spinner"></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {/* ═══════════════════════════════════════════════════════════
                      Changed header to "National ID" since applicationNumber
                      is now the client's national ID
                      ═══════════════════════════════════════════════════════════ */}
                  <th>National ID</th>
                  <th>Client</th>
                  <th>Product</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apps.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center p-4 text-muted">No loan applications found.</td>
                  </tr>
                )}
                
                {apps.map(row => (
                  <tr 
                    key={row._id} 
                    onClick={() => navigate(`/loans/applications/${row._id}/review`)} 
                    style={{cursor: 'pointer'}}
                  >
                    {/* ═══════════════════════════════════════════════════════════
                        Display applicationNumber (which is the National ID)
                        in monospace font like the client list
                        ═══════════════════════════════════════════════════════════ */}
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{row.applicationNumber}</td>
                    <td>
                      <div>{row.client ? `${row.client.firstName} ${row.client.lastName}` : 'N/A'}</div>
                      <div className="text-xs text-muted">{row.client?.phone}</div>
                    </td>
                    <td>{row.loanProduct?.name}</td>
                    <td>
                      <div>{formatCurrency(row.requestedAmount)}</div>
                      {row.approvedAmount && row.approvedAmount !== row.requestedAmount && (
                        <div className="text-xs text-success">Approved: {formatCurrency(row.approvedAmount)}</div>
                      )}
                    </td>
                    <td><StatusBadge status={row.status} map={statusMap} /></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {renderActions(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && (
          <div className="flex justify-between items-center text-sm text-muted mt-2 p-2" style={{borderTop: '1px solid #eee'}}>
            <span>Page {page} of {pagination.pages} (Total: {pagination.total})</span>
            <div className="flex gap-1">
              <button type="button" className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <button type="button" className="btn btn-sm btn-outline" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={`Admin Edit: ${editingApp?.applicationNumber || ''}`} size="600px">
        {editingApp && (
          <form onSubmit={handleEditSubmit}>
            <div style={{background: '#fff3cd', padding: 10, borderRadius: 6, marginBottom: 15, border: '1px solid #ffc107', fontSize: '13px'}}>
              <strong>⚠️ Admin Mode:</strong> Changing these values will directly update the application record.
            </div>

            <div className="grid grid-2 gap-1">
              <div className="form-group">
                <label>Loan Product</label>
                <select className="form-control" value={editForm.loanProduct} onChange={e => setEditForm({...editForm, loanProduct: e.target.value})}>
                  <option value="">Select Product</option>
                  {products.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Status Override</label>
                <select className="form-control" value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}>
                  {allStatuses.map(s => <option key={s} value={s}>{s.replace(/-/g, ' ').toUpperCase()}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-2 gap-1">
              <div className="form-group">
                <label>Requested Amount (KES)</label>
                <input type="number" className="form-control" value={editForm.requestedAmount} onChange={e => setEditForm({...editForm, requestedAmount: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Duration (Days)</label>
                <input type="number" className="form-control" value={editForm.duration} onChange={e => setEditForm({...editForm, duration: e.target.value})} />
              </div>
            </div>

            <div className="form-group">
              <label>Purpose</label>
              <input className="form-control" value={editForm.purpose} onChange={e => setEditForm({...editForm, purpose: e.target.value})} />
            </div>

            <div className="form-group">
              <label>Internal Notes / Override Reason</label>
              <textarea className="form-control" rows="3" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} placeholder="Why is this being changed?"></textarea>
            </div>

            <div className="flex gap-1 mt-2">
              <button type="submit" className="btn btn-warning" disabled={editLoading}>
                {editLoading ? 'Saving...' : '💾 Save Changes'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}